import { configureStore } from '@reduxjs/toolkit'
import { persistStore, persistReducer, REHYDRATE } from 'redux-persist'
import type { AnyAction, Middleware } from '@reduxjs/toolkit'
import storage from 'redux-persist/lib/storage'

import { sentryMiddleware } from './middleware/sentryMiddleware'
import { authReducer } from './slices/authSlice'
import { rehydrationComplete } from './slices/authSlice'
import { fieldMappingReducer } from './slices/fieldMappingSlice'
import { learnReducer, learnSlice } from './slices/learnSlice'

const authPersistConfig = {
    key: 'auth',
    storage,
    blacklist: ['ability'], // Don't persist ability - will be rebuilt on rehydration
}

const fieldMappingPersistConfig = {
    key: 'fieldMapping',
    storage,
}

const learnPersistConfig = {
    key: 'learn',
    storage,
    // Persist all learn data automatically - all data is serializable
}

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer)
const persistedFieldMappingReducer = persistReducer(fieldMappingPersistConfig, fieldMappingReducer)
const persistedLearnReducer = persistReducer(learnPersistConfig, learnReducer)

// Rehydration logic is now handled inline in middleware array

export const store = configureStore({
    reducer: {
        auth: persistedAuthReducer,
        fieldMapping: persistedFieldMappingReducer,
        learn: persistedLearnReducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
                ignoredPaths: ['auth.ability'], // Ignore CASL ability instance
            },
        }).concat([
            sentryMiddleware,
            ((storeAPI) => (next) => (action: AnyAction) => {
                // Handle rehydration complete for auth and learn slices
                if (action.type === REHYDRATE && 'key' in action) {
                    if (action.key === 'auth') {
                        storeAPI.dispatch(rehydrationComplete())
                    }
                    if (action.key === 'learn') {
                        storeAPI.dispatch(learnSlice.actions.rehydrationComplete())
                    }
                }
                return next(action)
            }) as Middleware,
        ]),
    devTools: import.meta.env.DEV,
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
